// @ts-nocheck
import create from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { axios } from './axios';
import { trackEvent } from '@/utils/analytics';

const baseUrl = `${process.env.API_URL}/api/v1`;

const createAuthHeader = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const useOnboardingStore = create(
  immer(() => ({
    // Free text → structured setup { name, contestType, contests, divisions } (AI).
    suggestStructure: (getToken) => async (orgId, freeText, contestTypeHint) => {
      const token = await getToken();
      const { data } = await axios.post(
        `${baseUrl}/organizations/${orgId}/onboarding/suggest`,
        { freeText, contestTypeHint },
        createAuthHeader(token),
      );
      if (data?.status === 0) throw new Error(data?.error || 'Could not generate a setup');
      return data.result;
    },

    // Materialize the confirmed structure (Tournament+contests+divisions or Season+divisions).
    buildStructure: (getToken) => async (orgId, structure) => {
      const token = await getToken();
      const { data } = await axios.post(
        `${baseUrl}/organizations/${orgId}/onboarding/build`,
        structure,
        createAuthHeader(token),
      );
      if (data?.status === 0) throw new Error(data?.error || 'Could not create your structure');
      // Conversion: this endpoint is the ONE path that creates a live
      // registration (ADR 0037) — Get Started "Build another" and the
      // Competitions Add Tournament/League dialog both land here.
      if (structure?.registration) {
        trackEvent('registration_published', {
          contest_type: data.result?.contestType,
          open: structure.registration.open !== false,
        });
      }
      return data.result;
    },

    // INT-0043 "Who's playing?" step: pasted teams per division + optional
    // first game date → real DivisionTeams + a drafted round-robin schedule.
    // Response: { divisions: [{ divisionId, teamIds, teamsCreated,
    // exampleTeamsRemoved, gamesCreated }], seasonStartsAt }.
    // The wizard's "go back and edit" (owner 2026-10-16): the newest
    // competition as an id-carrying tree, and its reconcile (rename / reorder
    // / add / remove; 409 when something holding real teams or games would go).
    getStructure: (getToken) => async (orgId) => {
      const token = await getToken();
      const { data } = await axios.get(`${baseUrl}/organizations/${orgId}/onboarding/structure`, createAuthHeader(token));
      if (data?.status === 0) throw new Error(data?.error || 'Could not load your competition');
      return data.result;
    },
    updateStructure: (getToken) => async (orgId, payload) => {
      const token = await getToken();
      const { data } = await axios.put(`${baseUrl}/organizations/${orgId}/onboarding/structure`, payload, createAuthHeader(token));
      if (data?.status === 0) throw new Error(data?.error || 'Could not save your changes');
      return data.result;
    },

    addTeams: (getToken) => async (orgId, payload) => {
      const token = await getToken();
      const { data } = await axios.post(
        `${baseUrl}/organizations/${orgId}/onboarding/teams`,
        payload,
        createAuthHeader(token),
      );
      if (data?.status === 0) throw new Error(data?.error || 'Could not save your teams');
      return data.result;
    },

    // INT-0052 — the divisions step's "Where you play" card: venue rows →
    // Locations (+ court sub-venues), TimeSlots for the newest competition,
    // and every time-less game of it placed. Response: { venues, slotsCreated,
    // slotsSkipped, gamesPlaced, gamesUnplaced, lastGameDate, firstSlotDate }.
    addVenues: (getToken) => async (orgId, payload) => {
      const token = await getToken();
      const { data } = await axios.post(
        `${baseUrl}/organizations/${orgId}/onboarding/venues`,
        payload,
        createAuthHeader(token),
      );
      if (data?.status === 0) throw new Error(data?.error || 'Could not save your venues');
      return data.result;
    },

    // INT-0051 — "I have a schedule": the previewed, grouped schedule → new
    // divisions + teams + games in one commit.
    importSchedule: (getToken) => async (orgId, payload) => {
      const token = await getToken();
      const { data } = await axios.post(
        `${baseUrl}/organizations/${orgId}/onboarding/schedule`,
        payload,
        createAuthHeader(token),
      );
      if (data?.status === 0) {
        const detail = Array.isArray(data?.rowErrors) && data.rowErrors.length ? ` (${data.rowErrors[0].message})` : '';
        throw new Error(`${data?.error || 'Could not import your schedule'}${detail}`);
      }
      return data.result;
    },
  })),
);

export default useOnboardingStore;
